import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { apiService, getApiErrorMessage } from '../services/apiService';

interface MegrendeloData {
  cegnev: string;
  iranyitoszam: string;
  cim: string;
  adoszam: string;
  cegjegyzekszam: string;
  bankszamlaszam: string;
  kepviselo: string;
}

interface AddMegrendeloDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddMegrendeloDialog: React.FC<AddMegrendeloDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<MegrendeloData>({
    cegnev: '',
    iranyitoszam: '',
    cim: '',
    adoszam: '',
    cegjegyzekszam: '',
    bankszamlaszam: '',
    kepviselo: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (field: keyof MegrendeloData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.cegnev.trim()) {
      setError('Cégnév megadása kötelező!');
      return;
    }

    if (!formData.adoszam.trim()) {
      setError('Adószám megadása kötelező!');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create the data object for the API
      const megrendeloData = {
        id: 0, // This will be auto-generated
        torzsszam: '0', // This will be set to the same as the generated ID
        cegnev: formData.cegnev.trim(),
        iranyitoszam: formData.iranyitoszam.trim(),
        cim: formData.cim.trim(),
        adoszam: formData.adoszam.trim(),
        cegjegyzekszam: formData.cegjegyzekszam.trim(),
        bankszamlaszam: formData.bankszamlaszam.trim(),
        kepviselo: formData.kepviselo.trim(),
      };

      await apiService.createData('megrendelok', megrendeloData);
      
      // Reset form
      setFormData({
        cegnev: '',
        iranyitoszam: '',
        cim: '',
        adoszam: '',
        cegjegyzekszam: '',
        bankszamlaszam: '',
        kepviselo: '',
      });
      
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error creating megrendelo:', err);
      setError(getApiErrorMessage(err, 'Hiba történt a megrendelő létrehozása során.'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      cegnev: '',
      iranyitoszam: '',
      cim: '',
      adoszam: '',
      cegjegyzekszam: '',
      bankszamlaszam: '',
      kepviselo: '',
    });
    setError(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>Új Megrendelő Hozzáadása</DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Cégnév *"
              fullWidth
              value={formData.cegnev}
              onChange={handleInputChange('cegnev')}
              error={!!error && !formData.cegnev.trim()}
              helperText={!!error && !formData.cegnev.trim() ? 'Cégnév megadása kötelező' : ''}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Irányítószám"
                fullWidth
                value={formData.iranyitoszam}
                onChange={handleInputChange('iranyitoszam')}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />

              <TextField
                label="Cím"
                fullWidth
                value={formData.cim}
                onChange={handleInputChange('cim')}
              />
            </Box>

            <TextField
              label="Adószám *"
              fullWidth
              value={formData.adoszam}
              onChange={handleInputChange('adoszam')}
              error={!!error && !formData.adoszam.trim()}
              helperText={!!error && !formData.adoszam.trim() ? 'Adószám megadása kötelező' : ''}
            />

            <TextField
              label="Cégjegyzékszám"
              fullWidth
              value={formData.cegjegyzekszam}
              onChange={handleInputChange('cegjegyzekszam')}
            />

            <TextField
              label="Bankszámlaszám"
              fullWidth
              value={formData.bankszamlaszam}
              onChange={handleInputChange('bankszamlaszam')}
            />

            <TextField
              label="Képviselő"
              fullWidth
              value={formData.kepviselo}
              onChange={handleInputChange('kepviselo')}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={handleCancel} 
          disabled={loading}
          variant="outlined"
        >
          Mégse
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={loading}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Létrehozás...' : 'Létrehozás'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMegrendeloDialog;
